import {
  AfterViewChecked,
  Component,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { GraphService } from 'src/app/services/graph/graph.service';
import { Error } from 'src/app/models/error.model';
import { GroupGraph } from 'src/app/models/group-graph.model';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { MatPaginator } from '@angular/material/paginator';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';
import { ToolBoxService } from 'src/app/services/toolbox/toolbox.service';

@Component({
  selector: 'app-groups-table',
  templateUrl: './groups-table.component.html',
  styleUrl: './groups-table.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class GroupsTableComponent
  implements OnInit, OnChanges, AfterViewChecked
{
  isBusy: boolean;
  microsoftGroupsUser: GroupGraph[];
  defaultColumns: string[] = ['photo', 'displayName', 'mail', 'view'];
  displayedColumnsGroups: string[] = [...this.defaultColumns];
  hiddenColumns: string[] = [];
  expandedUserGroup: GroupGraph | null;
  dataSourceGroups = new MatTableDataSource<GroupGraph>([]);

  groupsHasTeamsFilter: FormControl = new FormControl('');
  groupsNameFilter: FormControl = new FormControl('');
  groupsMailFilter: FormControl = new FormControl('');
  filterValuesGroups = {
    displayName: '',
    mail: '',
    hasTeam: false,
  };
  clickRow = false;

  spinner = {
    color: 'primary',
    mode: 'indeterminate' as ProgressSpinnerMode,
    value: 50,
    diameter: 50,
  };
  @Input() userId: string;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  constructor(
    private _graphSerivce: GraphService,
    private _router: Router,
    private _localStorageService: LocalStorageService,
    private _toolboxService: ToolBoxService
  ) {}

  ngAfterViewChecked() {
    if (!this.dataSourceGroups.sort || !this.dataSourceGroups.paginator) {
      this.dataSourceGroups.sort = this.sort;
      this.dataSourceGroups.paginator = this.paginator;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['userId'] && changes['userId'].currentValue) {
      this.getMicrosoftUserGroups(this.userId);
    }
  }

  ngOnInit() {
    this.setVisibleColumns();

    this.initGroupsFilter();

    if (this.microsoftGroupsUser) {
      this.initializeDataSourceGroups();
    } else if (this.userId && !this.isBusy) {
      this.getMicrosoftUserGroups(this.userId);
    }
  }

  redirectToGroup(groupId: string, fromRow: boolean) {
    if ((fromRow && this.clickRow) || !fromRow) {
      this._router.navigate(['/groups/' + groupId]);
      this._localStorageService.setItem('returnUrl', this._router.url);
    }
  }

  initializeDataSourceGroups() {
    this.dataSourceGroups = new MatTableDataSource(this.microsoftGroupsUser);
    this.dataSourceGroups.filterPredicate = this.createGroupsFilter();
  }

  async getMicrosoftUserGroups(userId: string) {
    try {
      this.isBusy = true;
      this.microsoftGroupsUser = await lastValueFrom(
        this._graphSerivce.getMicrosoftUserGroupsInfo(userId)
      );
      this.initializeDataSourceGroups();
    } catch (e) {
      let customError: Error = { error: undefined };
      if (e.error) {
        customError = JSON.parse(e.error);
      }
      if (customError.error !== undefined) {
        if (customError.error.code == 'ERR404') {
          this._router.navigate(['/errors/error-404']);
        }
      }
    } finally {
      this.isBusy = false;
    }
  }

  createGroupsFilter(): (data: GroupGraph, filter: string) => boolean {
    const filterFunction = (data, filter): boolean => {
      const searchTerms = JSON.parse(filter);
      return (
        data.displayName.toLowerCase().indexOf(searchTerms.displayName) !==
          -1 &&
        data.mail.toString().toLowerCase().indexOf(searchTerms.mail) !== -1 &&
        (!searchTerms.hasTeam || !data.hasTeam)
      );
    };
    return filterFunction;
  }

  initGroupsFilter() {
    this.groupsNameFilter.valueChanges.subscribe(name => {
      this.filterValuesGroups.displayName = name.toLowerCase();
      this.dataSourceGroups.filter = JSON.stringify(this.filterValuesGroups);
    });

    this.groupsMailFilter.valueChanges.subscribe(mail => {
      this.filterValuesGroups.mail = mail.toLowerCase();
      this.dataSourceGroups.filter = JSON.stringify(this.filterValuesGroups);
    });

    this.groupsHasTeamsFilter.valueChanges.subscribe(hasTeam => {
      this.filterValuesGroups.hasTeam = hasTeam;
      this.dataSourceGroups.filter = JSON.stringify(this.filterValuesGroups);
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.setVisibleColumns();
  }

  setVisibleColumns() {
    this.clickRow = false;
    this.displayedColumnsGroups = [...this.defaultColumns];
    this.hiddenColumns = [];
    this.expandedUserGroup = null;

    if (window.innerWidth < 850) {
      this.clickRow = true;
      this._toolboxService.hideTableColumnWithoutExpand(
        'view',
        this.displayedColumnsGroups
      );
    }
    if (window.innerWidth < 760) {
      this._toolboxService.hideTableColumn(
        'mail',
        this.displayedColumnsGroups,
        this.hiddenColumns
      );
    }
    if (window.innerWidth < 450) {
      this._toolboxService.hideTableColumnWithoutExpand(
        'photo',
        this.displayedColumnsGroups
      );
    }
  }
}
